/**
 * Importar mapas Universal VTT (`.uvtt`, `.dd2vtt`, `.df2vtt`: um JSON público que o Dungeondraft e
 * outros editores exportam), como o importador de mapas do original (`docs/spec/scene-walls.md`):
 * - a imagem embutida vira o fundo, já encaixada na grade pelo `pixels_per_grid`;
 * - `line_of_sight` e `objects_line_of_sight` viram paredes (linha que volta ao começo: fechada);
 * - `portals` viram portas de verdade (o original as fazia paredes fixas);
 * - `lights` viram tokens "só luz" na camada do mapa;
 * - a névoa e a visão dos tokens são ligadas.
 * As coordenadas do arquivo estão em células, a partir de `map_origin`.
 */
import { SIGHT_DEFAULTS, addTokenOps, backgroundOps, newSightOps, setOps, type SceneModel } from './model.js';
import { WALL_PRESETS, addWallOps, wallsContainerOps } from './walls.js';
import type { Op } from '../ndb.js';
import { translate, useLanguage } from '../i18n/index.js';
import { formatBytes } from '../bytes.js';

const say = (key: string, params?: Record<string, string | number>) => translate(useLanguage.getState().language, key, params);

type Pt = { x: number; y: number };
export type UvttMap = {
  origin: Pt;
  size: Pt;
  pixelsPerGrid: number;
  lines: Pt[][];
  portals: { a: Pt; b: Pt; closed: boolean }[];
  lights: { at: Pt; range: number }[];
  /** imagem em base64 */
  image: string;
};

export const UVTT_FILE = /\.(uvtt|dd2vtt|df2vtt)$/i;
/**
 * Maior arquivo aceito, a partir do maior arquivo do plano (`maxFileBytes`, docs/spec/planos.md): a
 * imagem vai embutida em base64 (4/3 do tamanho) e depois é enviada à mesa.
 */
export function uvttMaxBytes(maxFileBytes: number): number {
  return Math.ceil((maxFileBytes * 4) / 3) + 2 * 1024 * 1024;
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const pt = (v: unknown): Pt | null => {
  if (!v || typeof v !== 'object') return null;
  const x = num((v as Record<string, unknown>).x);
  const y = num((v as Record<string, unknown>).y);
  return x === null || y === null ? null : { x, y };
};
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

/** Lê o arquivo; erro com mensagem clara se não for um mapa UVTT. */
export function parseUvtt(text: string): UvttMap {
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(text) as Record<string, unknown>;
  } catch {
    throw new Error(say('scene.uvtt.notJson'));
  }
  const res = (data?.resolution ?? null) as Record<string, unknown> | null;
  const size = pt(res?.map_size);
  const ppg = num(res?.pixels_per_grid);
  if (!res || !size || !ppg || size.x <= 0 || size.y <= 0 || size.x > 400 || size.y > 400) throw new Error(say('scene.uvtt.noSize'));
  const image = typeof data.image === 'string' ? data.image.replace(/^data:[^,]*,/, '') : '';
  if (!image) throw new Error(say('scene.uvtt.noImage'));
  const lines = [...list(data.line_of_sight), ...list(data.objects_line_of_sight)]
    .map((line) => list(line).map(pt).filter((p): p is Pt => !!p))
    .filter((line) => line.length >= 2);
  const portals = list(data.portals).flatMap((p) => {
    const bounds = list((p as Record<string, unknown>)?.bounds).map(pt);
    if (bounds.length < 2 || !bounds[0] || !bounds[1]) return [];
    return [{ a: bounds[0], b: bounds[1], closed: (p as Record<string, unknown>).closed !== false }];
  });
  const lights = list(data.lights).flatMap((l) => {
    const at = pt((l as Record<string, unknown>)?.position);
    const range = num((l as Record<string, unknown>)?.range);
    return at && range && range > 0 ? [{ at, range }] : [];
  });
  return { origin: pt(res.map_origin) ?? { x: 0, y: 0 }, size, pixelsPerGrid: ppg, lines, portals, lights, image };
}

/**
 * Os bytes e o tipo da imagem embutida (PNG, WebP ou JPEG). `maxFileBytes`: o maior arquivo do plano;
 * a imagem vai para a mesa como arquivo, então o limite vale para ela.
 */
export function uvttImage(map: UvttMap, maxFileBytes = Number.POSITIVE_INFINITY): { bytes: Uint8Array; mime: string; ext: string } {
  const bin = atob(map.image);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  if (bytes.length > maxFileBytes) throw new Error(say('scene.uvtt.imageTooBig', { size: formatBytes(maxFileBytes) }));
  if (bytes[0] === 0x89 && bytes[1] === 0x50) return { bytes, mime: 'image/png', ext: 'png' };
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return { bytes, mime: 'image/jpeg', ext: 'jpg' };
  if (bin.slice(0, 4) === 'RIFF' && bin.slice(8, 12) === 'WEBP') return { bytes, mime: 'image/webp', ext: 'webp' };
  throw new Error(say('scene.uvtt.badImage'));
}

const r2 = (v: number) => Math.round(v * 100) / 100;

/**
 * As operações que montam a cena a partir do mapa (num lote só, que dá para desfazer).
 * `fileId` é a imagem já enviada para a mesa; `size`, o tamanho dela em px.
 */
export function uvttOps(map: UvttMap, scene: SceneModel, opts: { rootId: string; fileId: string; size: { width: number; height: number }; newId: () => string; lightName: string }): Op[] {
  const ops: Op[] = [...backgroundOps({ fileId: opts.fileId }, opts.size, { x: 0, y: 0, w: map.size.x, h: map.size.y })];
  let wallsId = scene.wallsId;
  if (!wallsId && (map.lines.length || map.portals.length)) {
    wallsId = opts.newId();
    ops.push(...wallsContainerOps(wallsId, opts.rootId));
  }
  const cell = (p: Pt): [number, number] => [r2(p.x - map.origin.x), r2(p.y - map.origin.y)];
  if (wallsId) {
    for (const line of map.lines) {
      const points = line.flatMap(cell);
      // linha que volta ao primeiro ponto: parede fechada (sem repetir o ponto)
      const closed = points.length >= 8 && points[0] === points[points.length - 2] && points[1] === points[points.length - 1];
      ops.push(...addWallOps(wallsId, opts.newId(), closed ? points.slice(0, -2) : points, closed, WALL_PRESETS.wall));
    }
    for (const portal of map.portals) {
      const id = opts.newId();
      ops.push(...addWallOps(wallsId, id, [...cell(portal.a), ...cell(portal.b)], false, WALL_PRESETS.door));
      if (!portal.closed) ops.push(...setOps(id, { open: true }));
    }
  }
  map.lights.forEach((light, i) => {
    const id = opts.newId();
    const [x, y] = cell(light.at);
    ops.push(...addTokenOps(id, { name: `${opts.lightName} ${i + 1}`, x: r2(x - 0.5), y: r2(y - 0.5), size: 1, layer: 'map' }, scene));
    ops.push(...setOps(id, { lightOnly: true }));
    // o alcance vem em células; a cena guarda na unidade dela
    ops.push(...newSightOps(id, { ...SIGHT_DEFAULTS, lightBright: r2(light.range * scene.cellDistance) }, opts.newId));
  });
  ops.push(...setOps('root', { fogEnabled: true, vision: true }));
  return ops;
}
